import { useEffect, useMemo, useReducer, useRef } from "react";
import lodash from "lodash";
import { useModal, useToast } from ".";
import { v4 as uuid } from "uuid";
import { comnUtils } from "../utils";

type TApi = (...args: any) => any;
type UseFetchProps = {
    api: TApi | TApi[];
    key?: any[];
    enabled?: boolean;
    showToast?: boolean;
    notifyStatus?: boolean;
    onSuccess?: (data?: any, params?: any) => void;
    onError?: (error?: any, params?: any) => void;
};
type UseFetchReturn = {
    data: any;
    isLoading: boolean;
    isSuccess: boolean;
    isError: boolean;
    fetch: (...args: any) => Promise<any> | undefined;
    setShowToast: (showToast: boolean) => void;
    getStatus: () => "loading" | "success" | "error" | "pending";
    getData: () => any;
    startInterval: (ms?: number) => any;
    finishInterval: () => any;
};

const createInitialState = (initialData: any) => {
    return {
        data: initialData,
        isLoading: false,
        isSuccess: false,
        isError: false,
    };
};

const reducer = (state: any, { type, payload }: any) => {
    switch (type) {
        case "loading":
            return { ...state, isLoading: true };
        case "error":
            return { ...state, isLoading: false, isError: true, isSuccess: false };
        case "success":
            return { ...state, isLoading: false, isError: false, isSuccess: true, data: payload };
    }
};

export const useFetch = (props: UseFetchProps): UseFetchReturn => {
    const { api, key = [], enabled, showToast = false, onSuccess, onError, notifyStatus } = props;

    const multi = Array.isArray(api);
    const initialData = multi ? Array(api.length).fill(undefined) : undefined;
    const modal = useModal();

    const toast = useToast();
    const ref = useRef<any>({
        toastUuid: uuid(),
        key: null,
        api,
        multi,
        data: initialData,
        isLoading: false,
        isSuccess: false,
        isError: false,
        toast: showToast,
        notifyStatus,
        onSuccess,
        onError,
        interval: null,
    });

    const [state, dispatch] = useReducer(reducer, initialData, createInitialState);
    const { data, isLoading, isSuccess, isError } = state;

    ref.current.api = api;
    ref.current.multi = Array.isArray(api);
    if (notifyStatus) ref.current.notifyStatus = notifyStatus;
    if (onSuccess) ref.current.onSuccess = onSuccess;
    if (onError) ref.current.onError = onError;

    const getData = () => {
        return ref.current.data;
    };
    const getStatus = () => {
        if (ref.current.isLoading) return "loading";
        if (ref.current.isSuccess) return "success";
        if (ref.current.isError) return "error";
        return "pending";
    };
    const setShowToast = (arg: boolean) => {
        ref.current.toast = arg;
    };
    const fetch = (...variables: any) => {
        if (ref.current.isLoading) return;
        ref.current.isLoading = true;
        if (ref.current.notifyStatus) dispatch({ type: "loading" });
        return new Promise(async (resolve, reject) => {
            const current = new Date();
            let _data;
            try {
                if (ref.current.multi) {
                    const response = await Promise.all(ref.current.api.map((_: any) => _(...variables)));
                    _data = response.map((_: any) => {
                        if (typeof _?.data === "object" && !Array.isArray(_?.data))
                            return Object.fromEntries(
                                Object.entries(_.data).map(([key, value]: any) => {
                                    return [key, { ...value, __t: current }];
                                }),
                            );
                        return _;
                    });
                } else {
                    const response = await ref.current.api(...variables);
                    if (response?.data instanceof ArrayBuffer) {
                        let filename = (response.headers["content-disposition"] ?? "")
                            .split("; ")
                            .find((str: any) => str?.startsWith("filename="));
                        if (filename) filename = filename.slice(9).replaceAll('"', "");
                        _data = { buffer: response.data, __t: current, filename };
                    } else if (typeof response?.data === "object" && !Array.isArray(response?.data)) {
                        _data = Object.fromEntries(
                            Object.entries(response.data).map(([key, value]: any) => {
                                return [key, { ...value, __t: current }];
                            }),
                        );
                    } else {
                        _data = response;
                    }
                }
            } catch (error: any) {
                if (ref.current.onError) {
                    // do nothing
                } else {
                    modal.openModal({
                        content: comnUtils.isEmpty(error?.response?.data?.errorMessage)
                            ? "msg.osc.00015"
                            : error?.response?.data?.errorMessage,
                    });
                }

                // content: comnUtils.isEmpty(e.response.data.errorMessage) ? MSG.RESULT_ERROR : e.response.data.errorMessage,
                /*
                if (error.response?.data?.errorCode == -999) {
                    if (ref.current.onError) {
                        // do nothing
                    } else {
                        modal.openModal({
                            content: error.response?.data?.errorMessage,
                        });
                    }
                } else {
                    if (ref.current.onError) {
                        // do nothing
                    } else {
                        modal.openModal({
                            content: error.message,
                        });
                    }
                }
                    */
                ref.current.isSuccess = false;
                ref.current.isLoading = false;
                ref.current.isError = true;
                if (ref.current.notifyStatus) dispatch({ type: "error" });
                if (ref.current.onError) ref.current.onError(error, variables);
                if (ref.current.toast)
                    toast.showToast({
                        type: "error",
                        content: "An error occurred",
                        uniqueId: ref.current.toastUuid,
                    });
                resolve(error);
                return;
            }
            ref.current.data = _data;
            ref.current.isError = false;
            ref.current.isLoading = false;
            ref.current.isSuccess = true;
            dispatch({ type: "success", payload: _data });
            if (ref.current.onSuccess) ref.current.onSuccess(_data, variables);
            if (ref.current.toast)
                toast.showToast({ type: "success", content: "msg.osc.00006", uniqueId: ref.current.toastUuid });
            resolve(_data);
        });
    };

    const startInterval = (ms?: number) => {
        if (!ms || ms < 1000) return;

        if (ref.current.interval) {
            clearInterval(ref.current.interval);
        }

        ref.current.interval = setInterval(() => {
            fetch();
        }, ms);
    };

    const finishInterval = () => {
        if (ref.current.interval === null) return;
        clearInterval(ref.current.interval);
    };

    useEffect(() => {
        if (!enabled) return;
        if (lodash.isEqual(ref.current.key, key)) return;
        ref.current.key = key;
        fetch();
    }, [enabled, ...key]);

    useEffect(() => {
        return () => {
            if (ref.current.interval) {
                clearInterval(ref.current.interval);
            }
        };
    }, []);

    return {
        data,
        isLoading,
        isSuccess,
        isError,
        fetch,
        getData,
        getStatus,
        setShowToast,
        startInterval,
        finishInterval,
    };
};
